/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Invoices Hooks - React Query for Invoice Data
 * ============================================================================
 *
 * Custom hooks for invoice data fetching and mutations using React Query.
 * Provides CRUD operations and status management for invoices.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  invoicesService,
  CreateInvoiceRequest,
  IssueInvoiceRequest,
  InvoiceStats,
  PaymentStatusResult,
} from '@/services/superadmin/invoices.service';
import { Invoice, InvoiceFilterParams, InvoicePreview } from '@/types/superadmin.types';
import { ApiError, PaginatedResponse } from '@/services/api/types';

// Query keys for cache management
export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  list: (filters?: InvoiceFilterParams & { page?: number; size?: number }) =>
    [...invoiceKeys.lists(), filters] as const,
  orgList: (orgId: string, page?: number) =>
    [...invoiceKeys.all, 'org', orgId, page] as const,
  details: () => [...invoiceKeys.all, 'detail'] as const,
  detail: (id: string) => [...invoiceKeys.details(), id] as const,
  byNumber: (invoiceNumber: string) =>
    [...invoiceKeys.all, 'number', invoiceNumber] as const,
  stats: (orgId?: string) => [...invoiceKeys.all, 'stats', orgId] as const,
  overdue: (page?: number) => [...invoiceKeys.all, 'overdue', page] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch all invoices with pagination (SuperAdmin only)
 */
export function useInvoices(page = 0, size = 20, sort = 'createdAt,desc') {
  return useQuery<PaginatedResponse<Invoice>, ApiError>({
    queryKey: invoiceKeys.list({ page, size, sort }),
    queryFn: () => invoicesService.getAllInvoices(page, size, sort),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch invoices for a specific organization
 */
export function useOrganizationInvoices(
  orgId: string,
  page = 0,
  size = 20,
  sort = 'createdAt,desc'
) {
  return useQuery<PaginatedResponse<Invoice>, ApiError>({
    queryKey: invoiceKeys.orgList(orgId, page),
    queryFn: () => invoicesService.getOrganizationInvoices(orgId, page, size, sort),
    enabled: !!orgId,
    staleTime: 1 * 60 * 1000,
  });
}

/**
 * Hook to search invoices with filters
 */
export function useSearchInvoices(
  filters: InvoiceFilterParams & { page?: number; size?: number; sort?: string }
) {
  return useQuery<PaginatedResponse<Invoice>, ApiError>({
    queryKey: invoiceKeys.list(filters),
    queryFn: () => invoicesService.searchInvoices(filters),
    staleTime: 30 * 1000, // 30 seconds for search results
  });
}

/**
 * Hook to fetch invoice by ID
 */
export function useInvoice(id: string) {
  return useQuery<Invoice, ApiError>({
    queryKey: invoiceKeys.detail(id),
    queryFn: () => invoicesService.getInvoiceById(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch invoice by invoice number
 */
export function useInvoiceByNumber(invoiceNumber: string) {
  return useQuery<Invoice, ApiError>({
    queryKey: invoiceKeys.byNumber(invoiceNumber),
    queryFn: () => invoicesService.getInvoiceByNumber(invoiceNumber),
    enabled: !!invoiceNumber,
  });
}

/**
 * Hook to fetch invoice statistics
 */
export function useInvoiceStats(orgId?: string) {
  return useQuery<InvoiceStats, ApiError>({
    queryKey: invoiceKeys.stats(orgId),
    queryFn: () => invoicesService.getInvoiceStats(orgId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch overdue invoices (SuperAdmin only)
 */
export function useOverdueInvoices(page = 0, size = 20) {
  return useQuery<PaginatedResponse<Invoice>, ApiError>({
    queryKey: invoiceKeys.overdue(page),
    queryFn: () => invoicesService.getOverdueInvoices(page, size),
    staleTime: 1 * 60 * 1000,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook for creating invoice from subscription
 */
export function useCreateInvoiceFromSubscription() {
  const queryClient = useQueryClient();

  return useMutation<
    Invoice,
    ApiError,
    { subscriptionId: string; request?: CreateInvoiceRequest }
  >({
    mutationFn: ({ subscriptionId, request }) =>
      invoicesService.createFromSubscription(subscriptionId, request),
    onSuccess: (newInvoice) => {
      // Invalidate lists to show new invoice
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.orgList(newInvoice.orgId),
      });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
    },
  });
}

/**
 * Hook for creating manual invoice
 */
export function useCreateManualInvoice() {
  const queryClient = useQueryClient();

  return useMutation<
    Invoice,
    ApiError,
    { orgId: string; request: CreateInvoiceRequest }
  >({
    mutationFn: ({ orgId, request }) =>
      invoicesService.createManualInvoice(orgId, request),
    onSuccess: (newInvoice) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.orgList(newInvoice.orgId),
      });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
    },
  });
}

/**
 * Hook for issuing a draft invoice
 */
export function useIssueInvoice() {
  const queryClient = useQueryClient();

  return useMutation<Invoice, ApiError, { id: string; request?: IssueInvoiceRequest }>({
    mutationFn: ({ id, request }) => invoicesService.issueInvoice(id, request),
    onSuccess: (updatedInvoice, { id }) => {
      // Update specific invoice in cache
      queryClient.setQueryData(invoiceKeys.detail(id), updatedInvoice);
      // Invalidate lists to reflect status change
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
    },
  });
}

/**
 * Hook for cancelling an invoice
 */
export function useCancelInvoice() {
  const queryClient = useQueryClient();

  return useMutation<Invoice, ApiError, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) => invoicesService.cancelInvoice(id, reason),
    onSuccess: (updatedInvoice, { id }) => {
      queryClient.setQueryData(invoiceKeys.detail(id), updatedInvoice);
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
    },
  });
}

/**
 * Hook for marking invoice as paid
 */
export function useMarkInvoicePaid() {
  const queryClient = useQueryClient();

  return useMutation<Invoice, ApiError, { id: string; paymentId?: string }>({
    mutationFn: ({ id, paymentId }) => invoicesService.markAsPaid(id, paymentId),
    onSuccess: (updatedInvoice, { id }) => {
      queryClient.setQueryData(invoiceKeys.detail(id), updatedInvoice);
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.overdue() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
    },
  });
}

/**
 * Hook for voiding an invoice (SuperAdmin only)
 */
export function useVoidInvoice() {
  const queryClient = useQueryClient();

  return useMutation<Invoice, ApiError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => invoicesService.voidInvoice(id, reason),
    onSuccess: (updatedInvoice, { id }) => {
      queryClient.setQueryData(invoiceKeys.detail(id), updatedInvoice);
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
    },
  });
}

/**
 * Hook for generating invoice PDF
 */
export function useGenerateInvoicePdf() {
  const queryClient = useQueryClient();

  return useMutation<Invoice, ApiError, string>({
    mutationFn: (id) => invoicesService.generatePdf(id),
    onSuccess: (updatedInvoice, id) => {
      queryClient.setQueryData(invoiceKeys.detail(id), updatedInvoice);
    },
  });
}

/**
 * Helper function to get PDF download URL
 */
export function getInvoicePdfUrl(id: string): string {
  return invoicesService.getPdfDownloadUrl(id);
}

/**
 * Hook for downloading invoice PDF (with authentication)
 */
export function useDownloadInvoicePdf() {
  return useMutation<void, ApiError, { id: string; filename?: string }>({
    mutationFn: ({ id, filename }) => invoicesService.downloadPdf(id, filename),
  });
}

/**
 * Helper function to download PDF directly (with authentication)
 */
export async function downloadInvoicePdf(id: string, filename?: string): Promise<void> {
  return invoicesService.downloadPdf(id, filename);
}

/**
 * Hook for getting invoice preview (theme-aware HTML)
 *
 * @param id Invoice ID
 * @param theme Theme preference ('light' or 'dark')
 * @param language Language code ('en' or 'fr')
 */
export function useInvoicePreview(
  id: string,
  theme: 'light' | 'dark' = 'dark',
  language = 'en'
) {
  return useQuery<InvoicePreview, ApiError>({
    queryKey: [...invoiceKeys.detail(id), 'preview', theme, language],
    queryFn: () => invoicesService.getInvoicePreview(id, theme, language),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes (HTML doesn't change often)
  });
}

/**
 * Hook for getting invoice HTML by variant
 *
 * @param id Invoice ID
 * @param variant Template variant
 * @param language Language code
 */
export function useInvoiceHtml(
  id: string,
  variant: 'blackops' | 'hybrid' | 'tactical' | 'stealth',
  language = 'en'
) {
  return useQuery<string, ApiError>({
    queryKey: [...invoiceKeys.detail(id), 'html', variant, language],
    queryFn: () => invoicesService.getInvoiceHtml(id, variant, language),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for downloading invoice PDF with specific variant
 */
export function useDownloadInvoicePdfVariant() {
  return useMutation<
    void,
    ApiError,
    {
      id: string;
      variant: 'blackops' | 'hybrid' | 'tactical' | 'stealth';
      language?: string;
      filename?: string;
    }
  >({
    mutationFn: ({ id, variant, language = 'en', filename }) =>
      invoicesService.downloadPdfVariant(id, variant, language, filename),
  });
}

/**
 * Hook for checking invoice payment status.
 * Checks the linked payment's status with the payment gateway and updates the database.
 */
export function useCheckInvoicePaymentStatus() {
  const queryClient = useQueryClient();

  return useMutation<PaymentStatusResult, ApiError, { invoiceId: string; paymentId: string }>({
    mutationFn: ({ paymentId }) => invoicesService.checkPaymentStatus(paymentId),
    onSuccess: (_result, { invoiceId }) => {
      // Refresh the invoice to show updated status
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.stats() });
    },
  });
}
